import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ControlSystemsService } from './control-systems.service'; // Ajusta la ruta
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, catchError, EMPTY, map, Observable, ReplaySubject, tap, throwError } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { CSTokenResponse } from '../../models/cstoken-response';

@Injectable({
  providedIn: 'root'
})
export class AppInitService {


  private initialized = false;
  private readonly controlSystemNameSubject = new BehaviorSubject<string>('');
  controlSystemName$ = this.controlSystemNameSubject.asObservable();

  private readonly tokensReadySubject = new ReplaySubject<boolean>(1);
  public tokensReady$ = this.tokensReadySubject.asObservable();

  private readonly hasAccessSubject = new BehaviorSubject<boolean | null>(null);
  hasAccess$ = this.hasAccessSubject.asObservable();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly controlSystemsService: ControlSystemsService,
    private readonly titleService: Title,
    private readonly router: Router,
  ) { }

  public init(url?: string): void {
    if (this.initialized) return;
    this.initialized = true;

    // Parseo síncrono desde window.location.search para evitar el problema de
    // que route.queryParams emita {} vacío en la primera carga antes de que
    // Angular router haya resuelto la URL.
    const searchSource = url
      ? (url.includes('?') ? url.split('?')[1] : url)
      : window.location.search.substring(1);
    const windowParams = new URLSearchParams(searchSource);
    const snapshotParams = this.route.snapshot.queryParams;

    const token: string | null = snapshotParams['token'] || windowParams.get('token') || null;
    const controlsystemId: string | null = snapshotParams['controlsystemId'] || windowParams.get('controlsystemId') || null;
    const guid: string | null = snapshotParams['guid'] || windowParams.get('guid') || null;
    const appId: string | null = snapshotParams['appsId'] || windowParams.get('appsId') || null;

    const storedMicrosoftAccessToken = sessionStorage.getItem('microsoftAccessToken');
    if (!storedMicrosoftAccessToken && !token) {
      this.hasAccessSubject.next(false);
      this.tokensReadySubject.next(false);
      return;
    }

    if (token) {
      sessionStorage.setItem('microsoftAccessToken', token);
      sessionStorage.removeItem('controlSystemsAccessToken');
    }

    this.hasAccessSubject.next(true);

    if (controlsystemId) {
      sessionStorage.setItem('controlsystemId', controlsystemId);
      console.log('External ID recibido:', controlsystemId);
    }
    if (guid) {
      sessionStorage.setItem('guid', guid);
      console.log('External guid recibido:', guid);
    }
    if (appId) {
      sessionStorage.setItem('appId', appId);
      console.log('External appId recibido:', appId);
    }

    this.controlSystemsService.getAppSessions(guid ?? sessionStorage.getItem('guid') ?? '').pipe(
      map(response => {
        sessionStorage.setItem('account', response.session);
        const account = JSON.parse(decodeURIComponent(response.session));
        sessionStorage.setItem('username', account.username);
        if (token != null || !sessionStorage.getItem('controlSystemsAccessToken')) {
          this.postControlSystemsToken$().subscribe({
            next: () => {
              console.log('[AppInit] postControlSystemsToken$ completed - tokens ready');
              this.tokensReadySubject.next(true);
            },
            error: (err) => {
              console.error('[AppInit] postControlSystemsToken$ failed', err);
              this.tokensReadySubject.next(false);
            }
          });
        } else {
          this.tokensReadySubject.next(true);
        }
      }),
      catchError((err: HttpErrorResponse) => {
        console.error('Error Control Systems Apps:', err.status);
        this.tokensReadySubject.next(false);
        return EMPTY;
      })
    ).subscribe();
  }

  public setTokenStatus(status: boolean): void {
    this.tokensReadySubject.next(status);
  }

  postControlSystemsToken$(): Observable<CSTokenResponse> {
    const microsoftAccessToken = sessionStorage.getItem('microsoftAccessToken');
    const controlsystemId = sessionStorage.getItem('controlsystemId');
    const appId = sessionStorage.getItem('appId');

    if (!microsoftAccessToken || !controlsystemId || !appId) {
      return throwError(() => new Error('Microsoft access token and control system identifiers are required.'));
    }

    return this.controlSystemsService.postControlSystemsToken(controlsystemId, appId, microsoftAccessToken).pipe(
      tap(res => {
        if (!res.access_token) {
          throw new Error('The control systems token response does not contain an access token.');
        }
        sessionStorage.setItem('controlSystemsAccessToken', res.access_token);
        sessionStorage.setItem('resource', res.resource);
        sessionStorage.setItem('slot', res.slot);
        sessionStorage.setItem('controlsystem', res.controlsystem);
        sessionStorage.setItem('controlsystemname', res.controlsystemname);
        sessionStorage.setItem('roles', res.roles);
        sessionStorage.setItem('customer', res.customer);
        sessionStorage.setItem('customername', res.customername);
        sessionStorage.setItem('factory', res.factory);
        sessionStorage.setItem('factoryname', res.factoryname);
        this.titleService.setTitle(`PIDDiagramGenius ${res.customername} - ${res.factoryname} `);
        this.updateControlSystemName(res.customername, res.factoryname, res.controlsystemname);
      }),
      catchError(err => {
        console.error('Error Control Systems Token:', err instanceof HttpErrorResponse ? err.status : 'Token exchange failed');
        return throwError(() => err);
      })
    );
  }

  updateControlSystemName(customername: string = '', factoryname: string = '', controlsystemname: string = ''): void {
    const name = `${customername} - ${factoryname} - ${controlsystemname}`;
    this.controlSystemNameSubject.next(name);
  }

}
